import type { VariableDefinition } from '@repo/videa-components/src/ui/variable-textarea/types';
import {
  normalizeMultiValueDisplay,
  renderTemplate,
  valueForDisplay,
} from '@repo/videa-components/src/ui/variable-textarea/useContentEditable';
import { REQUIRED_EMPTY_PLACEHOLDER } from '@repo/videa-components/src/ui/variable-textarea/utils';

export { renderTemplate };
export { REQUIRED_EMPTY_PLACEHOLDER };

/**
 * Shared template/field types for voice notes (API shape).
 * Used by both client and chrome extension.
 */
export interface VideaNoteTemplateSectionSnapshot {
  sectionId: string;
  key: string;
  label: string;
  description: string;
  bodyTemplate: string;
}

export interface VideaNoteFieldValue {
  key: string;
  originalValue: string;
  editedValue: string | null;
  defaultValue: string | number | boolean | null;
  required?: boolean;
  constraints: {
    availableValues: string[];
  } | null;
}

export interface VideaNoteSectionFieldsSnapshot {
  sectionId: string;
  key: string;
  label: string;
  description: string;
  fields: VideaNoteFieldValue[];
}

/**
 * Minimal note shape with optional template/fields (compatible with ClinicalNote and extension note).
 */
export interface NoteWithTemplateData {
  id?: string;
  summary: string;
  editedSummary: string | null;
  originalTemplate?: VideaNoteTemplateSectionSnapshot[] | null;
  editedTemplate?: VideaNoteTemplateSectionSnapshot[] | null;
  fields?: VideaNoteSectionFieldsSnapshot[] | null;
}

/**
 * Gets the current template content (editedTemplate or originalTemplate).
 */
export function getCurrentTemplate(
  note: NoteWithTemplateData | null | undefined,
): VideaNoteTemplateSectionSnapshot[] | null {
  return note?.editedTemplate ?? note?.originalTemplate ?? null;
}

/**
 * Gets the current field values from the note.
 */
export function getCurrentFields(
  note: NoteWithTemplateData | null | undefined,
): VideaNoteSectionFieldsSnapshot[] | null {
  return note?.fields ?? null;
}

/**
 * Gets the current value for a field/variable.
 * Priority: editedValue > defaultValue > REQUIRED_EMPTY_PLACEHOLDER (if required) or '___'
 */
export function getFieldValue(field: {
  editedValue: string | number | boolean | null;
  defaultValue: string | number | boolean | null;
  required?: boolean;
}): string {
  if (field.editedValue !== null) {
    return String(field.editedValue);
  }
  if (field.defaultValue !== null) {
    return String(field.defaultValue);
  }
  return field.required ? REQUIRED_EMPTY_PLACEHOLDER : '___';
}

/**
 * Extracts variable definitions from fields data.
 */
export function extractVariableDefinitions(
  fields: VideaNoteSectionFieldsSnapshot[] | null | undefined,
): VariableDefinition[] {
  if (!fields?.length) return [];

  const seenKeys = new Set<string>();
  const definitions: VariableDefinition[] = [];
  const allFields = fields.flatMap((section) => section.fields);

  for (const field of allFields) {
    if (!seenKeys.has(field.key)) {
      seenKeys.add(field.key);
      const defaultValue = getFieldValue(field);
      definitions.push({
        varName: field.key,
        defaultValue,
        allValues: field.constraints?.availableValues || [defaultValue],
      });
    }
  }

  return definitions;
}

/**
 * Derives the editable note content: template body for template notes, else summary.
 */
export function getNoteContent(
  note: NoteWithTemplateData | null | undefined,
): string {
  if (!note) return '';
  const currentTemplate = getCurrentTemplate(note);
  if (currentTemplate && currentTemplate.length > 0) {
    const section = (note.editedTemplate ?? note.originalTemplate)?.[0];
    return section?.bodyTemplate ?? note.editedSummary ?? note.summary ?? '';
  }
  return note.editedSummary ?? note.summary ?? '';
}

/**
 * Evaluates Handlebars-style conditional blocks (`{{#if field}}...{{/if}}`) in a template
 * string using the provided variable values. Blocks whose guard variable is absent or
 * empty are removed; blocks with a non-empty value are replaced with their inner content
 * (preserving any `{{var}}` tokens inside for chip rendering). The loop handles nested
 * conditionals by re-scanning until the output stabilizes.
 * TODO: deprecate handling this on FE once FE moves away from using a template towards
 * using the (marked-up) summary directly from the BE.
 */
export function evaluateConditionals(
  template: string,
  values: Record<string, string>,
): string {
  let result = template;
  let prev: string;
  do {
    prev = result;
    // Only match leaf blocks — blocks whose inner content contains no nested {{#if.
    // This ensures the do/while processes from the innermost block outward.
    // Loop guaranteed to terminate b/c innermost if tags are removed at each iteration
    // When no tags are left to be removed the halting condition triggers.
    // Treating '___' and '! Required' as falsey (empty / placeholder)
    result = result.replace(
      /\{\{#if\s+([\w_]+)\}\}((?:(?!\{\{#if\s)[\s\S])*?)\{\{\/if\}\}/g,
      (_, varName: string, inner: string) => {
        const value = values[varName];
        const hasValue =
          value && value !== '___' && value !== REQUIRED_EMPTY_PLACEHOLDER;
        return hasValue ? inner : '';
      },
    );
  } while (result !== prev);
  return result;
}

/**
 * Payload for PATCH /videa-notes/:id/summary (template notes).
 */
export interface TemplateUpdatePayload {
  editedTemplate: Array<{ sectionId: string; bodyTemplate: string }>;
  editedSummary: string;
  fields: VideaNoteSectionFieldsSnapshot[];
  editedFields?: Array<{
    sectionId: string;
    fields: Array<{ key: string; editedValue: string | null }>;
  }>;
}

export type TemplateEditedFields = NonNullable<
  TemplateUpdatePayload['editedFields']
>;

/**
 * Builds the update body for a template note (editedTemplate, editedSummary, fields, editedFields).
 */
export function buildTemplateUpdatePayload(
  content: string,
  variableValues: Record<string, string>,
  fullNote: NoteWithTemplateData,
  editedFieldsOverride?: TemplateEditedFields | null,
): TemplateUpdatePayload | null {
  const currentTemplate = getCurrentTemplate(fullNote);
  const currentFields = getCurrentFields(fullNote);
  const firstSection = currentTemplate?.[0];
  if (!firstSection || !currentFields?.length) {
    return null;
  }
  const sectionId = firstSection.sectionId;
  const editedSummary = renderTemplate(content, variableValues);

  let editedFields: TemplateUpdatePayload['editedFields'];
  if (editedFieldsOverride && editedFieldsOverride.length > 0) {
    editedFields = editedFieldsOverride;
  } else {
    const derived: Array<{
      sectionId: string;
      fields: Array<{ key: string; editedValue: string | null }>;
    }> = [];
    for (const section of currentFields) {
      const fieldEdits: Array<{ key: string; editedValue: string | null }> = [];
      for (const field of section.fields) {
        const currentVal = variableValues[field.key];
        const defaultValue = getFieldValue(field);
        // Compare in display format: currentVal may be internal ("A\u001DB"),
        // defaultValue is always display ("A, B"). Normalize so "A, B" and "B, A" compare equal.
        const currentDisplay =
          currentVal !== undefined ? valueForDisplay(currentVal) : undefined;
        const normalizedCurrent =
          currentDisplay !== undefined
            ? normalizeMultiValueDisplay(currentDisplay)
            : undefined;
        const normalizedDefault = normalizeMultiValueDisplay(
          defaultValue ?? '',
        );
        if (
          normalizedCurrent !== undefined &&
          normalizedCurrent !== normalizedDefault
        ) {
          fieldEdits.push({
            key: field.key,
            editedValue:
              currentDisplay === REQUIRED_EMPTY_PLACEHOLDER
                ? null
                : (currentDisplay ?? null),
          });
        }
      }
      if (fieldEdits.length > 0) {
        derived.push({ sectionId: section.sectionId, fields: fieldEdits });
      }
    }
    editedFields = derived.length > 0 ? derived : undefined;
  }

  return {
    editedTemplate: [{ sectionId, bodyTemplate: content }],
    editedSummary,
    fields: currentFields,
    ...(editedFields && editedFields.length > 0 && { editedFields }),
  };
}
